import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface TimeRange {
  start: number;
  end: number;
  description?: string;
}

interface AudioContextProps {
  audioUrl: string;
  setAudioUrl: (url: string) => void;
  isPlaying: boolean;
  togglePlayPause: () => void;
  audioElement: HTMLAudioElement | null;
  selectedTimeRange: TimeRange | null;
  setSelectedTimeRange: (range: TimeRange | null) => void;
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
}

// Create a single persistent audio element outside the component lifecycle
const globalAudioElement = typeof window !== 'undefined' ? new Audio() : null;
if (globalAudioElement) {
  globalAudioElement.preload = 'auto';
  globalAudioElement.autoplay = false;
  if ('webkitAudioContext' in window) {
    globalAudioElement.setAttribute('webkit-playsinline', 'true');
    globalAudioElement.setAttribute('playsinline', 'true');
  }
  console.log('Created global audio element');
}

const AudioContext = createContext<AudioContextProps>({
  audioUrl: '',
  setAudioUrl: () => {},
  isPlaying: false,
  togglePlayPause: () => {},
  audioElement: null,
  selectedTimeRange: null,
  setSelectedTimeRange: () => {},
  playbackRate: 1,
  setPlaybackRate: () => {},
});

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange | null>(null);
  const [playbackRate, setPlaybackRate] = useState<number>(1);
  
  // Update audio source when URL changes
  useEffect(() => {
    if (!globalAudioElement) return;
    
    console.log('Setting new audio URL:', audioUrl);
    
    if (audioUrl) {
      if (globalAudioElement.src !== audioUrl) {
        try {
          // Pause any playing audio before changing source
          if (!globalAudioElement.paused) {
            globalAudioElement.pause();
          }
          
          // Clear any existing source
          globalAudioElement.src = '';
          globalAudioElement.load();
          
          // Set new source after a short delay
          setTimeout(() => {
            if (globalAudioElement) {
              globalAudioElement.src = audioUrl;
              globalAudioElement.load();
              console.log('Audio source updated and loading started');
            }
          }, 100);
          
          setIsPlaying(false);
          setSelectedTimeRange(null); // Reset selected time range when changing audio
        } catch (error) {
          console.error('Error updating audio source:', error);
          setIsPlaying(false);
        }
      }
    } else {
      // Clear the audio source when URL is empty
      try {
        globalAudioElement.pause();
        globalAudioElement.src = '';
        globalAudioElement.load();
      } catch (error) {
        console.error('Error clearing audio source:', error);
      }
      setIsPlaying(false);
      setSelectedTimeRange(null);
    }
  }, [audioUrl]);

  // Handle playback rate changes
  useEffect(() => {
    if (!globalAudioElement) return;
    globalAudioElement.playbackRate = playbackRate;
  }, [playbackRate]);

  // Handle play/pause
  useEffect(() => {
    if (!globalAudioElement || !audioUrl) return;
    
    const playAudio = async () => {
      try {
        if (isPlaying) {
          console.log('Context: Playing audio');
          // Ensure src is set before playing
          if (!globalAudioElement.src) {
            globalAudioElement.src = audioUrl;
            globalAudioElement.load();
          }
          await globalAudioElement.play();
        } else {
          console.log('Context: Pausing audio');
          globalAudioElement.pause();
        }
      } catch (error) {
        console.error('Playback error in context:', error);
        setIsPlaying(false);
      }
    };
    
    playAudio();
  }, [isPlaying, audioUrl]);

  const togglePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  return (
    <AudioContext.Provider
      value={{
        audioUrl,
        setAudioUrl,
        isPlaying,
        togglePlayPause,
        audioElement: globalAudioElement,
        selectedTimeRange,
        setSelectedTimeRange,
        playbackRate,
        setPlaybackRate,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => useContext(AudioContext); 